import { useQueryClient } from "@tanstack/react-query";
import type { CustomerAnnotations, CustomerWithDetails } from "@estelle/domain";

import { DIRECTORY_KEYS } from "@/modules/contacts/directory/keys";

/**
 * The cache keys a customer lives under.
 *
 * One place, so the list, the detail and the writes that touch both cannot
 * disagree about what to refresh.
 */
export const CUSTOMER_KEYS = {
  list: ["customers", "list"] as const,
  detail: (id: string) => ["customers", "detail", id] as const,
};

/**
 * What to do when an annotation write is accepted: put the server's answer in
 * the open customer, and refetch the list.
 *
 * The screen takes the note and tags the *response* carries, not what it
 * submitted — a tag typed in a different case comes back in the vocabulary's
 * spelling, and an empty note comes back as no note. The list is invalidated
 * rather than patched, because the note and the tags are columns in it and a
 * filter on either may now include or exclude this customer. All Contacts
 * lists the same customer with the same tags, so its list is refreshed too.
 */
export function useCustomerAnnotations(customerId: string) {
  const queryClient = useQueryClient();
  return {
    accepted(annotations: CustomerAnnotations) {
      queryClient.setQueryData<CustomerWithDetails | null>(
        CUSTOMER_KEYS.detail(customerId),
        (current) => (current ? { ...current, ...annotations } : current),
      );
      void queryClient.invalidateQueries({ queryKey: CUSTOMER_KEYS.list });
      void queryClient.invalidateQueries({ queryKey: DIRECTORY_KEYS.list });
    },
  };
}
